"use client"

import { getAuthHeader } from "../utils/auth"; // ★ 1. 認証ヘッダー取得関数をインポート
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useOutletContext } from "react-router-dom"
import { JAPAN_GTM_ASSISTANT_URL, isEnglishOperatorMode } from "../utils/operatorMode";
import {
  getOperatorPrefectureLabelFromAddress,
  getOperatorTaxonomyLabel,
  getOperatorTaxonomyLabelsFromText,
  getOperatorTaxonomySearchText,
} from "../utils/operatorTaxonomyLabels";


// --- SVG Icons ---
const SendIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>;
const SearchIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>;
const LoadingSpinner = () => (
  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

// --- Constants ---
const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

// --- Helper & UI Components ---
function normalizeNgText(value) {
  return String(value || "").trim().normalize("NFKC").toLowerCase();
}

function normalizeNgDomain(value) {
  if (!value) return "";
  let raw = String(value).trim().normalize("NFKC").toLowerCase();
  if (!raw) return "";
  try {
    raw = new URL(raw.includes("://") ? raw : `https://${raw}`).hostname;
  } catch (e) {
    raw = raw.split("/")[0];
  }
  return raw.replace(/^www\./, "");
}

function getDomain(url) {
  return normalizeNgDomain(url);
}

function renderMyHistoryStatus(status, isEnglishBeta = false) {
  const normalizedStatus = String(status || "").toLowerCase();

  if (!normalizedStatus) {
    return <span className="text-gray-400">{isEnglishBeta ? "Not started" : "未送信"}</span>;
  }

  if (normalizedStatus === "success") {
    return <span className="text-green-500 font-bold">{isEnglishBeta ? "Submitted" : "✅ 成功"}</span>;
  }

  if (normalizedStatus.includes("review")) {
    return <span className="text-yellow-500 font-bold">{isEnglishBeta ? "Needs Review" : "⚠️ 要確認"}</span>;
  }

  if (
    normalizedStatus.startsWith("fail") ||
    ["no_form_found", "dead_url", "wrong_form_type", "sales_rejected"].includes(normalizedStatus)
  ) {
    return <span className="text-red-500 font-bold">{isEnglishBeta ? "Failed" : "❌ 失敗"}</span>;
  }

  return <span className="text-red-500 font-bold">{isEnglishBeta ? "Failed" : "❌ 失敗"}</span>;
}

function Pagination({ currentPage, totalPages, hasNextPage = false, isEstimatedTotal = false, onPageChange, isLoading, isEnglishBeta = false }) {
  const canGoNext = Boolean(hasNextPage) || currentPage < totalPages;
  if (totalPages <= 1 && !canGoNext) return null;
  const displayTotalPages = isEstimatedTotal ? `${totalPages}+` : totalPages;
  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <button onClick={() => onPageChange(1)} disabled={currentPage === 1 || isLoading} className="min-w-[4.5rem] px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{isEnglishBeta ? "First" : "最初"}</button>
      <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading} className="min-w-[4.5rem] px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{isEnglishBeta ? "Previous" : "前へ"}</button>
      <span className="px-2 text-sm text-gray-700">{currentPage} / {displayTotalPages}</span>
      <button onClick={() => onPageChange(currentPage + 1)} disabled={!canGoNext || isLoading} className="min-w-[4.5rem] px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{isEnglishBeta ? "Next" : "次へ"}</button>
      {!isEstimatedTotal && (
        <button onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages || isLoading} className="min-w-[4.5rem] px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{isEnglishBeta ? "Last" : "最後"}</button>
      )}
    </div>
  );
}

const OperatorTaxonomyLabel = ({ value, labelType, className = "" }) => {
    const englishLabel = getOperatorTaxonomyLabel(value, labelType);
    if (!englishLabel) return <span>{value}</span>;

    return (
        <span className={`leading-tight ${className}`}>
            <span className="block">{value}</span>
            <span className="block text-[11px] font-normal text-gray-500">{englishLabel}</span>
        </span>
    );
};

const OperatorTaxonomyText = ({ value, labelType }) => {
    const englishLabels = getOperatorTaxonomyLabelsFromText(value, labelType, 3);
    if (!englishLabels.length) return <>{value}</>;

    return (
        <span className="leading-tight">
            <span className="block truncate">{value}</span>
            <span className="block truncate text-[11px] text-gray-500">{englishLabels.join(" / ")}</span>
        </span>
    );
};

const OperatorAddressText = ({ value }) => {
    const englishLabel = getOperatorPrefectureLabelFromAddress(value);
    if (!englishLabel) return <>{value}</>;

    return (
        <span className="leading-tight">
            <span className="block truncate">{value}</span>
            <span className="block truncate text-[11px] text-gray-500">{englishLabel}</span>
        </span>
    );
};

const FilterModal = ({ title, options, selected, onSave, onClose, isAddress = false, isEnglishBeta = false, taxonomyType = "" }) => {
    const [currentSelection, setCurrentSelection] = useState(selected);
    const [optionSearchTerm, setOptionSearchTerm] = useState("");
    const normalizedOptionSearchTerm = optionSearchTerm.trim().toLowerCase();

    const optionMatches = (...values) => {
        if (!normalizedOptionSearchTerm) return true;
        return values.some(value => {
            const searchText = isEnglishBeta
                ? `${value || ""} ${getOperatorTaxonomySearchText(value, taxonomyType)}`
                : value;
            return String(searchText || "").toLowerCase().includes(normalizedOptionSearchTerm);
        });
    };

    const handleCheckboxChange = (group, value) => {
        setCurrentSelection(prev => {
            const newGroupSelection = prev[group] ? [...prev[group]] : [];
            if (newGroupSelection.includes(value)) {
                const filteredGroup = newGroupSelection.filter(item => item !== value);
                const newSelection = { ...prev, [group]: filteredGroup };
                if (filteredGroup.length === 0) {
                    delete newSelection[group];
                }
                return newSelection;
            } else {
                return { ...prev, [group]: [...newGroupSelection, value] };
            }
        });
    };

    const handleGroupCheckboxChange = (group, values) => {
        setCurrentSelection(prev => {
            const newSelection = { ...prev };
            const currentGroupValues = newSelection[group] || [];
            const allValuesInGroup = values.map(v => v.name || v);
            const areAllSelected = allValuesInGroup.every(v => currentGroupValues.includes(v));

            if (areAllSelected) {
                delete newSelection[group];
            } else {
                newSelection[group] = allValuesInGroup;
            }
            return newSelection;
        });
    };
    
    const handleSimpleCheckboxChange = (value) => {
        setCurrentSelection(prev => {
            const newSelection = [...prev];
            if (newSelection.includes(value)) {
                return newSelection.filter(item => item !== value);
            } else {
                return [...newSelection, value];
            }
        });
    };

    const handleSimpleGroupChange = (groupValues) => {
         setCurrentSelection(prev => {
            const areAllSelected = groupValues.every(v => prev.includes(v));
            if (areAllSelected) {
                return prev.filter(item => !groupValues.includes(item));
            } else {
                return [...new Set([...prev, ...groupValues])];
            }
        });
    };

    const totalSelectedCount = isAddress
        ? Object.values(currentSelection).reduce((acc, curr) => acc + curr.length, 0)
        : currentSelection.length;

    const renderOptions = () => {
        if (isAddress) {
            return Object.entries(options).map(([region, prefectures]) => {
                const visiblePrefectures = Object.entries(prefectures).reduce((acc, [pref, cities]) => {
                    const visibleCities = optionMatches(region, pref)
                        ? cities
                        : cities.filter(city => optionMatches(city));
                    if (visibleCities.length > 0) acc[pref] = visibleCities;
                    return acc;
                }, {});
                if (Object.keys(visiblePrefectures).length === 0) return null;
                return (
                <div key={region} className="mb-4">
                    <h4 className="text-lg font-semibold text-gray-700 border-b pb-2 mb-2">
                        {isEnglishBeta ? <OperatorTaxonomyLabel value={region} labelType="region" /> : region}
                    </h4>
                    <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                        {Object.entries(visiblePrefectures).map(([pref, cities]) => (
                            <div key={pref}>
                                <div className="font-bold text-indigo-700 mb-1 flex items-center">
                                    <input
                                        type="checkbox"
                                        id={`group-${pref}`}
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        checked={cities.length > 0 && cities.every(city => (currentSelection[pref] || []).includes(city))}
                                        onChange={() => handleGroupCheckboxChange(pref, cities)}
                                    />
                                    <label htmlFor={`group-${pref}`} className="ml-2">
                                        {isEnglishBeta ? <OperatorTaxonomyLabel value={pref} labelType="prefecture" /> : pref}
                                    </label>
                                </div>
                                <div className="pl-5 space-y-1">
                                    {cities.map(city => (
                                        <div key={city} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id={`address-${city}-${pref}`}
                                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                checked={(currentSelection[pref] || []).includes(city)}
                                                onChange={() => handleCheckboxChange(pref, city)}
                                            />
                                            <label htmlFor={`address-${city}-${pref}`} className="ml-2 text-sm text-gray-700">{city}</label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )});
        }
        return Object.entries(options).map(([category, values]) => {
            const visibleValues = optionMatches(category)
                ? values
                : values.filter(value => optionMatches(value));
            if (visibleValues.length === 0) return null;
            return (
            <div key={category} className="mb-4">
                <h4 className="text-lg font-semibold text-gray-700 border-b pb-2 mb-2 flex items-center">
                     <input
                        type="checkbox"
                        id={`group-${category}`}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={visibleValues.every(v => currentSelection.includes(v))}
                        onChange={() => handleSimpleGroupChange(visibleValues)}
                    />
                    <label htmlFor={`group-${category}`} className="ml-2">
                        {isEnglishBeta ? <OperatorTaxonomyLabel value={category} labelType={taxonomyType} /> : category}
                    </label>
                </h4>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {visibleValues.map(value => (
                        <div key={value} className="flex items-center">
                            <input
                                type="checkbox"
                                id={`val-${value}`}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                checked={currentSelection.includes(value)}
                                onChange={() => handleSimpleCheckboxChange(value)}
                            />
                            <label htmlFor={`val-${value}`} className="ml-2 text-sm text-gray-700">
                                {isEnglishBeta ? <OperatorTaxonomyLabel value={value} labelType={taxonomyType} /> : value}
                            </label>
                        </div>
                    ))}
                </div>
            </div>
        )});
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3 sm:p-4">
            <div className="bg-white text-gray-900 rounded-2xl shadow-2xl w-full max-w-5xl h-full max-h-[92dvh] flex flex-col sm:rounded-lg">
                <header className="p-4 border-b flex justify-between items-start gap-3 flex-shrink-0">
                    <h3 className="text-lg font-bold text-gray-800 sm:text-xl">{title}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl">&times;</button>
                </header>
                <main className="p-4 overflow-y-auto flex-grow sm:p-6">
                    <div className="sticky top-0 z-10 mb-4 bg-white pb-3">
                        <input
                            type="search"
                            value={optionSearchTerm}
                            onChange={(event) => setOptionSearchTerm(event.target.value)}
                            placeholder={isEnglishBeta ? "Search options..." : "項目を検索..."}
                            className="w-full rounded-md border border-gray-300 bg-white p-2 text-sm text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                    </div>
                    {renderOptions()}
                </main>
                <footer className="p-4 border-t flex flex-col gap-3 bg-gray-50 flex-shrink-0 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-indigo-600 font-semibold">{isEnglishBeta ? `${totalSelectedCount.toLocaleString()} selected` : `${totalSelectedCount.toLocaleString()}件 選択中`}</span>
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
                        <button onClick={onClose} className="py-2 px-4 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 font-semibold">{isEnglishBeta ? "Cancel" : "キャンセル"}</button>
                        <button onClick={() => onSave(currentSelection)} className="py-2 px-4 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 font-semibold">{isEnglishBeta ? "Apply selection" : "選択を反映"}</button>
                    </div>
                </footer>
            </div>
        </div>
    );
};

// --- Main Component ---
function SendPage() {
  const { user, activeJob, setActiveJob, isMaintenanceMode } = useOutletContext();
  const isEnglishBeta = isEnglishOperatorMode();

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [salesList, setSalesList] = useState([]);
  const [ngList, setNgList] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [selectedItems, setSelectedItems] = useState(new Map());
  const [clientParams, setClientParams] = useState({});
  const [previewItem, setPreviewItem] = useState(null);
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewBody, setPreviewBody] = useState("");
  const [savedLists, setSavedLists] = useState([]);
  const [selectedSavedListId, setSelectedSavedListId] = useState("");
  const [listSourceType, setListSourceType] = useState('master');
  const [isRawMode, setIsRawMode] = useState(false);

  const initialFilters = {
    industry_class: [], address: {}, business_tags: [], list_tags: [],
    searchTerm: "", sentStatus: "all", globalClickStatus: "all",
    minGlobalSends: "", globalSendStatus: "all", industryRelatedSearch: false,
  };
  const [filters, setFilters] = useState(initialFilters);
  const [activeFilters, setActiveFilters] = useState(initialFilters);

  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [isCountingTotal, setIsCountingTotal] = useState(false);
  const [totalCountError, setTotalCountError] = useState("");
  const totalCountRequestRef = useRef(0);
  const [filterOptions, setFilterOptions] = useState({ industry: {}, business_tags: {}, addresses: {} });
  const [addressOptions, setAddressOptions] = useState({});
  const [isModalOpen, setIsModalOpen] = useState({ industry_class: false, address: false, business_tags: false });

  const [jobRights, setJobRights] = useState({ total_job_slots: 1, total_processes: 1, processes_per_job_slot: 3 });
  const [currentUsage, setCurrentUsage] = useState({ used_processes: 0 });
  const [jobSlotsToUse, setJobSlotsToUse] = useState(1);
  
  const [usageStatus, setUsageStatus] = useState(null);

  const selectedClientId = useMemo(() => (user?.role === "admin" || user?.role === "admin_staff" ? localStorage.getItem("selectedClientId") || "" : user?.client_id), [user]);
  const selectedTemplate = useMemo(() => templates.find((t) => String(t.id) === selectedTemplateId) || {}, [templates, selectedTemplateId]);
  const selectedSavedList = useMemo(() => savedLists.find((l) => String(l.id) === String(selectedSavedListId)) || null, [savedLists, selectedSavedListId]);

  const checkIsNg = useCallback((item) => {
    if (!item || !ngList || ngList.length === 0) return false;
    const itemDomain = item.url ? getDomain(item.url) : null;
    const itemCompany = normalizeNgText(item.company);
    for (const ngItem of ngList) {
      if (normalizeNgText(ngItem.company) && itemCompany && normalizeNgText(ngItem.company) === itemCompany) return true;
      if (ngItem.domain && itemDomain && normalizeNgDomain(ngItem.domain) === itemDomain) return true;
      if (ngItem.url_pattern && itemDomain && normalizeNgDomain(ngItem.url_pattern) === itemDomain) return true;
    }
    return false;
  }, [ngList]);

  const fetchData = useCallback(async (page, limit, currentActiveFilters, currentListSourceType) => {
    if (!user) return;
    const clientIdToFetch = user.role === "admin" ? localStorage.getItem("selectedClientId") || "" : user.client_id;
    if (user.role === "admin" && !clientIdToFetch) {
      setSalesList([]); setPagination(null); setSearchError(""); return;
    }
    setIsLoading(true);
    setSearchError("");
    setTotalCountError("");
    try {
      const payload = {
        client_id: clientIdToFetch,
        page,
        limit,
        filters: currentActiveFilters,
        listSourceType: currentListSourceType,
      };

      if (currentListSourceType === 'master' && selectedSavedListId) {
          try {
            const savedListRes = await fetch(`${API_URL}/api/saved-lists/${selectedSavedListId}`, {
              headers: getAuthHeader(), // ★ 2. 認証ヘッダーを追加
            });
            const data = await savedListRes.json();
            if (data.success && data.list.target_ids) {
              payload.targetIds = data.list.target_ids;
            } else {
              payload.targetIds = [];
            }
          } catch (error) {
          const message = isEnglishBeta ? "Could not load the selected saved list." : "選択されたリストの読み込みに失敗しました。";
          setSearchError(message);
          setSalesList([]);
          setPagination(null);
          alert(message); return;
          }
      }
      
      const res = await fetch(`${API_URL}/api/load`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() }, // ★ 2. 認証ヘッダーを追加
        body: JSON.stringify(payload) });
      let result = {};
      try {
        result = await res.json();
      } catch (jsonError) {
        console.error("検索結果JSONの解析に失敗:", jsonError);
      }

      if (res.ok && result.success) {
        setSalesList(result.data || []);
        setPagination(result.pagination || null);
        const shouldResolveTotal = result.pagination?.isEstimatedTotal || result.pagination?.hasNextPage;
        if (shouldResolveTotal) {
          const requestId = totalCountRequestRef.current + 1;
          totalCountRequestRef.current = requestId;
          setIsCountingTotal(true);
          fetch(`${API_URL}/api/load/count`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...getAuthHeader() },
            body: JSON.stringify(payload)
          })
            .then(async (countRes) => {
              let countResult = {};
              try {
                countResult = await countRes.json();
              } catch (jsonError) {
                console.error("総件数JSONの解析に失敗:", jsonError);
              }
              if (!countRes.ok || !countResult.success) {
                throw new Error(countResult.error || (isEnglishBeta ? "Could not confirm the full target count." : "総件数の確認に失敗しました。"));
              }
              return countResult;
            })
            .then((countResult) => {
              if (totalCountRequestRef.current !== requestId) return;
              const totalItems = Number(countResult.totalItems) || 0;
              setPagination((prev) => {
                if (!prev) return prev;
                const totalPages = totalItems === 0 ? 0 : Math.ceil(totalItems / prev.itemsPerPage);
                return {
                  ...prev,
                  totalItems,
                  totalPages,
                  hasNextPage: prev.currentPage < totalPages,
                  isEstimatedTotal: false
                };
              });
              setTotalCountError("");
            })
            .catch((countError) => {
              if (totalCountRequestRef.current !== requestId) return;
              console.error("総件数の確認に失敗:", countError);
              setTotalCountError(countError.message || (isEnglishBeta ? "Could not confirm the full target count." : "総件数の確認に失敗しました。"));
            })
            .finally(() => {
              if (totalCountRequestRef.current === requestId) setIsCountingTotal(false);
            });
        } else {
          totalCountRequestRef.current += 1;
          setIsCountingTotal(false);
          setTotalCountError("");
        }
      } else {
        const fallbackMessage = isEnglishBeta
          ? "Search is taking too long. Reduce the filters and try again."
          : "検索に時間がかかっています。条件を減らして再検索してください。";
        const message = result.error || fallbackMessage;
        console.error("リストの読み込みに失敗:", message);
        setSearchError(message);
        totalCountRequestRef.current += 1;
        setIsCountingTotal(false);
        setTotalCountError("");
        setSalesList([]); setPagination(null);
      }
    } catch (err) {
      console.error("営業リストの取得エラー:", err);
      setSearchError(isEnglishBeta
        ? "Search is taking too long. Reduce the filters and try again."
        : "検索に時間がかかっています。条件を減らして再検索してください。");
      totalCountRequestRef.current += 1;
      setIsCountingTotal(false);
      setTotalCountError("");
      setSalesList([]); setPagination(null);
    } finally {
      setIsLoading(false);
    }
  }, [user, selectedSavedListId, isEnglishBeta]);

  const fetchUsageAndRights = useCallback(async () => {
    if (!selectedClientId) return;
    try {
        const [usageRes, rightsRes, usageStatusRes] = await Promise.all([
            fetch(`${API_URL}/api/client/current-usage?client_id=${selectedClientId}`, { headers: getAuthHeader() }), // ★ 2. 認証ヘッダーを追加
            fetch(`${API_URL}/api/client/job-rights?client_id=${selectedClientId}`, { headers: getAuthHeader() }), // ★ 2. 認証ヘッダーを追加
            fetch(`${API_URL}/api/usage-status?clientId=${selectedClientId}`, { headers: getAuthHeader() }) // ★ 2. 認証ヘッダーを追加
        ]);
        const usageData = await usageRes.json();
        const rightsData = await rightsRes.json();
        const usageStatusData = await usageStatusRes.json();

        if (usageData.success) setCurrentUsage(usageData);
        if (rightsData.success) {
            setJobRights(rightsData);
        }
        if (usageStatusData.success) {
            setUsageStatus(usageStatusData);
        }

    } catch (error) {
        console.error("Failed to fetch usage and rights:", error);
    }
  }, [selectedClientId]);

  useEffect(() => {
    fetchUsageAndRights();
    const intervalId = setInterval(fetchUsageAndRights, 15000);
    return () => clearInterval(intervalId);
  }, [fetchUsageAndRights]);

  const fetchOtherData = useCallback(() => {
    if (!selectedClientId) return;
    Promise.all([
      fetch(`${API_URL}/api/templates?client_id=${selectedClientId}`, { headers: getAuthHeader() }).then((res) => res.json()), // ★ 2. 認証ヘッダーを追加
      fetch(`${API_URL}/api/ng-list?client_id=${selectedClientId}`, { headers: getAuthHeader() }).then((res) => res.json()), // ★ 2. 認証ヘッダーを追加
      fetch(`${API_URL}/api/client-params?client_id=${selectedClientId}`, { headers: getAuthHeader() }).then((res) => res.json()), // ★ 2. 認証ヘッダーを追加
      fetch(`${API_URL}/api/saved-lists?client_id=${selectedClientId}`, { headers: getAuthHeader() }).then((res) => res.json()), // ★ 2. 認証ヘッダーを追加
      fetch(`${API_URL}/api/search-filters`, { headers: getAuthHeader() }).then((res) => res.json()), // ★ 2. 認証ヘッダーを追加
      fetch(`${API_URL}/api/address-master`, { headers: getAuthHeader() }).then((res) => res.json()), // ★ 2. 認証ヘッダーを追加
    ])
      .then(([templatesData, ngListData, paramsData, savedListsData, searchFiltersData, addressFiltersData]) => {
        if (templatesData.success) {
          const sortedTemplates = (templatesData.templates || []).sort((a, b) => b.id - a.id);
          setTemplates(sortedTemplates);
          if (sortedTemplates.length > 0) setSelectedTemplateId(String(sortedTemplates[0].id));
        }
        if (ngListData.success) setNgList(ngListData.data || []);
        if (paramsData.success) setClientParams(paramsData.params || {});
        if (savedListsData.success) setSavedLists(savedListsData.lists || []);
        if (searchFiltersData.success) {
            setFilterOptions({
                industry: searchFiltersData.filters['業種'] || {},
                business_tags: searchFiltersData.filters['事業タグ'] || {},
            });
        }
        if (addressFiltersData.success) setAddressOptions(addressFiltersData.data || {});
      })
      .catch((err) => console.error("Ancillary data fetch error:", err));
  }, [selectedClientId]);

  useEffect(() => { fetchOtherData() }, [selectedClientId, fetchOtherData]);

  useEffect(() => {
    if (previewItem && selectedTemplate.id && selectedClientId) {
      setPreviewSubject("プレビューを生成中..."); setPreviewBody("...");
      const payload = { template_id: selectedTemplate.id, sales_list_id: previewItem.id, client_id: selectedClientId, list_type: listSourceType, ...clientParams };
      // ★★★ 提案: 認証ヘッダーを追加 ★★★
      fetch(`${API_URL}/api/templates/preview`, { 
          method: "POST", 
          headers: { "Content-Type": "application/json", ...getAuthHeader() }, // 認証ヘッダーを追加
          body: JSON.stringify(payload) })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) { setPreviewSubject(data.subject); setPreviewBody(data.body); }
          else { setPreviewSubject("プレビュー生成失敗"); setPreviewBody(data.error); }
        })
        .catch((err) => { setPreviewSubject("プレビュー生成失敗"); setPreviewBody(err.message); });
    }
  }, [previewItem, selectedTemplate.id, selectedClientId, listSourceType]);

  const handleSearch = () => {
    setHasSearched(true); 
    setSearchError("");
    setCurrentPage(1);
    setActiveFilters(filters);
    fetchData(1, itemsPerPage, filters, listSourceType);
    setSelectedItems(new Map());
  };

  const handleModalSave = (filterName, selection) => {
    setFilters(prev => ({ ...prev, [filterName]: selection }));
    setIsModalOpen(prev => ({ ...prev, [filterName]: false }));
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleListSourceChange = (e) => {
    const newListSourceType = e.target.value;
    setListSourceType(newListSourceType);
    setFilters(initialFilters); 
    setActiveFilters(initialFilters);
    setSelectedItems(new Map()); 
    setCurrentPage(1); 
    setSelectedSavedListId("");
    setSalesList([]); 
    setPagination(null); 
    setSearchError("");
    setIsCountingTotal(false);
    setTotalCountError("");
    totalCountRequestRef.current += 1;
    setHasSearched(false);
  };
  
  const handlePageChange = (newPage) => {
    const canMoveToKnownPage = pagination && newPage <= pagination.totalPages;
    const canMoveToNextEstimatedPage = pagination?.hasNextPage && newPage === currentPage + 1;
    if (newPage > 0 && pagination && (canMoveToKnownPage || canMoveToNextEstimatedPage) && !isLoading) {
      setCurrentPage(newPage);
      fetchData(newPage, itemsPerPage, activeFilters, listSourceType);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = Number(e.target.value);
    if (!isLoading) {
      setItemsPerPage(newItemsPerPage); 
      setCurrentPage(1);
      fetchData(1, newItemsPerPage, activeFilters, listSourceType);
    }
  };

  const remainingSends = useMemo(() => {
    if (!usageStatus || !usageStatus.limits) return Infinity;
    
    // null limit means no monthly target cap.
    if (usageStatus.limits.sends.limit === null) return Infinity;

    return usageStatus.limits.sends.limit - usageStatus.limits.sends.current;
  }, [usageStatus]);

  const isTrialOpenTargetCap = usageStatus?.plan === 'trial' && usageStatus?.limits?.sends?.limit === null;

  const handleToggle = (item) => {
    if (checkIsNg(item)) return; 

    setSelectedItems((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(item.id)) {
        newMap.delete(item.id);
      } else {
        if (newMap.size < remainingSends) {
          newMap.set(item.id, item);
        } else {
          alert(isEnglishBeta
            ? `Monthly target cap reached. Remaining targets this month: ${remainingSends}`
            : `送信上限に達しているため、これ以上選択できません。\n今月の残り送信可能件数: ${remainingSends}件`);
        }
      }
      return newMap;
    });
  };

  const handleSelectAllOnPage = () => {
    if (salesList.length > remainingSends) {
        alert(isEnglishBeta
          ? `Selecting this page (${salesList.length} targets) would exceed your monthly target cap (${remainingSends} remaining).`
          : `このページの全件(${salesList.length}件)を選択すると、送信上限(${remainingSends}件)を超えてしまいます。`);
        return;
    }
    setSelectedItems((prev) => {
      const newMap = new Map(prev);
      salesList.forEach((item) => { if (!checkIsNg(item)) newMap.set(item.id, item) });
      return newMap;
    });
  };

  const handleClearAll = () => setSelectedItems(new Map());

  const handleSend = async () => {
    const selectedTargets = Array.from(selectedItems.values());
    const useSelectedItems = selectedTargets.length > 0;
    let confirmationMessage = "";
    let numToSend = 0;

    if (useSelectedItems) {
      numToSend = selectedTargets.length;
      confirmationMessage = isEnglishBeta
        ? `Start governed outreach for ${numToSend.toLocaleString()} selected targets?`
        : `選択した ${numToSend.toLocaleString()} 件の送信を開始しますか？`;
    } else {
      numToSend = pagination ? pagination.totalItems : 0;
      if (numToSend === 0) return alert(isEnglishBeta ? "No eligible targets match the current filters." : "現在のフィルター条件に合致する送信対象がありません。");
      if (pagination?.isEstimatedTotal) {
        return alert(isEnglishBeta
          ? "The full target count is still being resolved. Select targets on the page, or narrow the filters before executing all matches."
          : "検索結果の総件数がまだ確定していません。このページから対象を選択するか、条件を絞ってから全件送信してください。");
      }
      confirmationMessage = isEnglishBeta
        ? `Execute all ${numToSend.toLocaleString()} targets matching the current filters?`
        : `現在のフィルター条件に合致する ${numToSend.toLocaleString()} 件の送信をすべて開始しますか？`;
    }
    
    if(numToSend > remainingSends) {
        return alert(isEnglishBeta
          ? `Target count exceeds your monthly cap.\nRemaining targets: ${remainingSends}\nSelected targets: ${numToSend}`
          : `送信数が上限を超えています。\n残り送信可能件数: ${remainingSends}件\n選択された件数: ${numToSend}件`);
    }

    if (!selectedTemplate.id) return alert(isEnglishBeta ? "Select a Japanese outreach template first." : "テンプレートを選択してください。");
    if (activeJob) return alert(isEnglishBeta ? "Another execution job is currently running." : "現在、別のバックグラウンド処理が実行中です。");

    // --- ▼▼▼ ここからが修正箇所です ▼▼▼ ---
    // プレビューが生成されていれば previewSubject を使い、
    // されていなければ選択中のテンプレートの件名(selectedTemplate.subject)をフォールバックとして使用します。
    const subjectForConfirm = previewSubject || selectedTemplate.subject || (isEnglishBeta ? "(No subject)" : "（件名なし）");
    const lanesToUse = Math.max(1, Number(jobSlotsToUse) || 1);
    const hourlyCapacity = lanesToUse * 600;
    const estimatedHours = hourlyCapacity > 0 ? numToSend / hourlyCapacity : 0;
    const estimatedTimeLabel = estimatedHours <= 1
      ? (isEnglishBeta ? "within about 1 hour" : "約1時間以内")
      : (isEnglishBeta ? `about ${Math.ceil(estimatedHours)} hours` : `約${Math.ceil(estimatedHours)}時間`);
    const targetSourceLabel = selectedSavedList
      ? selectedSavedList.name
      : (listSourceType === 'client'
        ? (isEnglishBeta ? "My uploaded list / current filters" : "マイリスト / 現在の検索条件")
        : (isEnglishBeta ? "SalesBreaker DB / current filters" : "弊社提供リスト / 現在の検索条件"));
    const confirmationDetails = isEnglishBeta
      ? [
          confirmationMessage,
          "",
          `Template: ${selectedTemplate.title || selectedTemplate.id}`,
          `Target source: ${targetSourceLabel}`,
          `Target count: ${numToSend.toLocaleString()}`,
          `Execution lanes: ${lanesToUse} lane(s)`,
          `Estimated speed: ${hourlyCapacity.toLocaleString()} targets/hour`,
          `Estimated completion: ${estimatedTimeLabel}`,
          `Subject preview: ${subjectForConfirm}`,
          "",
          "Clicks and replies usually need time to accumulate. Review analytics about 2 days after execution."
        ].join("\n")
      : [
          confirmationMessage,
          "",
          `文面テンプレート: ${selectedTemplate.title || selectedTemplate.id}`,
          `送付先: ${targetSourceLabel}`,
          `対象件数: ${numToSend.toLocaleString()}件`,
          `使用する送信枠: ${lanesToUse}枠`,
          `概算スピード: 毎時${hourlyCapacity.toLocaleString()}件`,
          `完了目安: ${estimatedTimeLabel}`,
          `件名例: ${subjectForConfirm}`,
          "",
          "クリックや反応はすぐには揃いません。送信後2日ほど置いてから分析する想定です。"
        ].join("\n");
    
    if (!window.confirm(confirmationDetails)) return;
    // --- ▲▲▲ 修正はここまでです ▲▲▲ ---

    try {
      const baseClientParams = {
        client_id: selectedClientId,
        template_id: selectedTemplate.id, // ★ ここにIDを追加！
        template_title: selectedTemplate.title,
        template_subject: selectedTemplate.subject,
        template_body: selectedTemplate.body,
        company_profile_id: selectedTemplate.company_profile_id,
        ...clientParams,
      };

      const bodyPayload = { 
          clientParams: baseClientParams, 
          listSourceType: listSourceType,
          isRawMode: isRawMode // ★ ここを追加
      };

      if (listSourceType === 'master' && selectedSavedListId) {
        bodyPayload.saved_list_id = Number(selectedSavedListId);
      }
      
      bodyPayload.requested_job_slots = jobSlotsToUse;

      if (useSelectedItems) {
        bodyPayload.targetIds = Array.from(selectedItems.keys());
      } else {
        bodyPayload.filters = activeFilters;
      }

      const res = await fetch(`${API_URL}/api/jobs/start/send-campaign`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() }, // ★ 2. 認証ヘッダーを追加
        body: JSON.stringify(bodyPayload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `サーバーエラー (ステータス: ${res.status})`);
      
      setActiveJob({ id: data.jobId, status: "pending" });
      alert(isEnglishBeta ? "Outreach queued. Monitor execution progress from the notification panel." : "送信処理を受け付けました。進捗は画面右下の通知をご確認ください。");
      handleClearAll(); 
      handleSearch(); 
      fetchOtherData();
    } catch (err) {
      console.error("送信ジョブ受付エラー:", err); alert(`${isEnglishBeta ? "Error" : "エラー"}: ${err.message}`);
    }
  };
  
  const availableSlots = useMemo(() => {
      const processesPerSlot = jobRights.processes_per_job_slot || 6;
      if (processesPerSlot === 0) return 0;
      const remainingProcesses = jobRights.total_processes - currentUsage.used_processes;
      return Math.floor(remainingProcesses / processesPerSlot);
  }, [jobRights, currentUsage]);

  const isEstimatedResultCount = Boolean(pagination?.isEstimatedTotal);
  const isAllMatchesSendPending = selectedItems.size === 0 && (isEstimatedResultCount || isCountingTotal);

  const isSendButtonDisabled = useMemo(() => {
    if (!!activeJob || !selectedTemplate.id || availableSlots <= 0) return true;
    if (searchError) return true;
    if (isAllMatchesSendPending) return true;

    const numToSend = selectedItems.size > 0 ? selectedItems.size : (pagination?.totalItems || 0);
    if (numToSend === 0) return true;
    if (numToSend > remainingSends) return true;

    return false;
  }, [activeJob, selectedTemplate.id, pagination, availableSlots, selectedItems.size, remainingSends, searchError, isAllMatchesSendPending]);

  if (isMaintenanceMode && user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center h-[70vh] text-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-4">{isEnglishBeta ? "Execution temporarily unavailable" : "⚙️ メンテナンス中です"}</h1>
          <p className="text-gray-600">{isEnglishBeta ? "Outreach execution is currently in maintenance mode." : "現在、送信機能はご利用いただけません。"}</p>
        </div>
      </div>
    );
  }

  const countSelected = (filterName) => {
     if (filterName === 'address') {
         return Object.values(filters.address).reduce((sum, cities) => sum + cities.length, 0);
     }
     return filters[filterName]?.length || 0;
  }

  return (
    <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
      {isModalOpen.industry_class && <FilterModal title={isEnglishBeta ? "Filter by industry" : "業種で絞り込み"} options={filterOptions.industry} selected={filters.industry_class} onSave={(selection) => handleModalSave('industry_class', selection)} onClose={() => setIsModalOpen(p => ({...p, industry_class: false}))} isEnglishBeta={isEnglishBeta} taxonomyType="industry" />}
      {isModalOpen.address && <FilterModal title={isEnglishBeta ? "Filter by location" : "住所で絞り込み"} options={addressOptions} selected={filters.address} onSave={(selection) => handleModalSave('address', selection)} onClose={() => setIsModalOpen(p => ({...p, address: false}))} isAddress={true} isEnglishBeta={isEnglishBeta} taxonomyType="address"/>}
      {isModalOpen.business_tags && <FilterModal title={isEnglishBeta ? "Filter by business tags" : "事業タグで絞り込み"} options={filterOptions.business_tags} selected={filters.business_tags} onSave={(selection) => handleModalSave('business_tags', selection)} onClose={() => setIsModalOpen(p => ({...p, business_tags: false}))} isEnglishBeta={isEnglishBeta} taxonomyType="businessTag" />}

      <div className="max-w-screen-2xl mx-auto">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">{isEnglishBeta ? "Start Outreach" : "📤 フォーム送信"}</h1>
            {isEnglishBeta && (
              <p className="mt-2 max-w-3xl text-sm text-gray-600">
                Review Japanese outreach copy, select eligible Japanese companies, allocate execution lanes, and start operator-approved outreach.
              </p>
            )}
          </div>
        </div>
        {isEnglishBeta && (
          <div className="mb-6 grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
              <p className="font-bold">Dedicated inbox recommended</p>
              <p className="mt-1">
                Replies, thank-you messages, and contact form notifications may arrive in volume.
                Use a dedicated inbox separate from your primary business email.
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700">
              <p className="font-bold text-slate-900">Japan-native source data</p>
              <p className="mt-1">
                Company data is preserved in its original Japanese format. English labels explain the workflow, but target names, locations, and categories may remain Japanese.
              </p>
            </div>
          </div>
        )}
        
        {usageStatus && usageStatus.limits && remainingSends !== Infinity && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-lg mb-6 text-sm">
                <p>
                    {isEnglishBeta ? (
                      <>
                        <span className="font-bold">{usageStatus.plan === 'free' ? 'Free plan' : 'Trial'}</span> target count cap is active.
                        Remaining targets this month: <span className="font-bold text-lg">{remainingSends.toLocaleString()}</span>
                      </>
                    ) : (
                      <>
                        <span className="font-bold">{usageStatus.plan === 'free' ? '無料プラン' : 'トライアル'}</span>のご利用ありがとうございます。
                        今月の残り送信可能件数: <span className="font-bold text-lg">{remainingSends.toLocaleString()}</span>件
                      </>
                    )}
                </p>
            </div>
        )}
        {isTrialOpenTargetCap && (
            <div className="bg-blue-50 border border-blue-200 text-blue-900 p-4 rounded-lg mb-6 text-sm">
                <p>
                    {isEnglishBeta ? (
                      <>
                        This trial does not currently enforce a monthly target count cap. Execution is still governed by lane capacity and policy checks.
                      </>
                    ) : (
                      <>
                        <span className="font-bold">トライアル</span>では、フォーム送信の件数制限を設けていません。
                      </>
                    )}
                </p>
            </div>
        )}

        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6">
           <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-2">{isEnglishBeta ? "1. Select target source" : "1. 送信対象リストを選択"}</h2>
            <div className="flex flex-col gap-3 sm:flex-row sm:gap-6 border-b pb-6">
              <label className="flex items-center space-x-2 cursor-pointer rounded-lg border border-gray-200 p-3 sm:border-0 sm:p-0">
                <input type="radio" name="listSource" value="master" checked={listSourceType === 'master'} onChange={handleListSourceChange} className="form-radio h-4 w-4 text-blue-600"/>
                <span className="text-gray-800">{isEnglishBeta ? "SalesBreaker Japanese company DB" : "弊社提供リスト"}</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer rounded-lg border border-gray-200 p-3 sm:border-0 sm:p-0">
                <input type="radio" name="listSource" value="client" checked={listSourceType === 'client'} onChange={handleListSourceChange} className="form-radio h-4 w-4 text-blue-600"/>
                <span className="text-gray-800">{isEnglishBeta ? "My list (uploaded targets)" : "マイリスト (アップロードしたリスト)"}</span>
              </label>
            </div>
            {isEnglishBeta && (
              <p className="mt-3 text-xs text-gray-500">
                SalesBreaker DB and uploaded target data may contain Japanese company names, addresses, industries, and tags by design.
              </p>
            )}
          </div>
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-2">{isEnglishBeta ? "2. Select Japanese outreach template" : "2. 文面テンプレートを選択"}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <select value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)} className="w-full p-2 border rounded-md bg-white text-gray-900">
                  <option value="">{isEnglishBeta ? "-- Select template --" : "-- テンプレートを選択 --"}</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
                {listSourceType === 'master' && (
                    <select value={selectedSavedListId} onChange={(e) => setSelectedSavedListId(e.target.value)} className="w-full p-2 border rounded-md bg-white text-gray-900">
                      <option value="">{isEnglishBeta ? "-- Optional saved list filter --" : "-- 保存済みリストで絞り込む (任意) --"}</option>
                      {savedLists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                )}
            </div>
            {jobRights.total_job_slots > 1 && (
              <div className="mt-4 pt-4 border-t">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isEnglishBeta ? (
                    <>Lane allocation (currently <span className="font-bold text-blue-600">{availableSlots}</span> / {jobRights.total_job_slots} lanes available)</>
                  ) : (
                    <>使用するジョブ枠数 (現在 <span className="font-bold text-blue-600">{availableSlots}</span> / {jobRights.total_job_slots}枠 利用可能)</>
                  )}
                </label>
                <div className="flex items-center">
                    <select 
                      value={jobSlotsToUse} 
                      onChange={(e) => setJobSlotsToUse(Number(e.target.value))}
                      disabled={availableSlots <= 0}
                      className="w-full md:w-1/2 p-2 border rounded-md bg-white text-gray-900 disabled:bg-gray-100"
                    >
                      {Array.from({ length: availableSlots > 0 ? availableSlots : 1 }, (_, i) => i + 1).map(slot => (
                        <option key={slot} value={slot}>
                          {isEnglishBeta ? `${slot} lane${slot > 1 ? 's' : ''}` : `${slot}枠`}
                        </option>
                      ))}
                    </select>
                    {availableSlots <= 0 && (
                      <p className="ml-4 text-sm text-red-600 font-semibold">
                        {isEnglishBeta ? "All execution lanes are currently allocated." : "現在、実行中のジョブで全ての枠を使用しています。"}
                      </p>
                    )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {isEnglishBeta ? "More lanes increase concurrent execution capacity for this outreach run." : "多くの枠を使用すると、より多くの送信を同時に進めるため、全体の完了時間が短縮されます。"}
                </p>
              </div>
            )}
          </div>
	                  <form className="pt-6 border-t" onSubmit={(event) => { event.preventDefault(); handleSearch(); }}>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-700">{isEnglishBeta ? "3. Filter eligible targets" : "3. 送信対象を絞り込み"}</h2>
              <button
                type="button"
                onClick={() => setFilters(initialFilters)}
                className="text-sm text-blue-600 hover:underline"
              >
                {isEnglishBeta ? "Clear filters" : "全フィルターをクリア"}
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
	                <input type="search" name="searchTerm" placeholder={isEnglishBeta ? "Keyword (company, URL, tags, location, etc.)" : "フリーワード (会社名、URLなど)"} value={filters.searchTerm} onChange={handleFilterChange} className="p-2 border rounded-md w-full md:col-span-2 bg-white text-gray-900 placeholder-gray-400"/>
	                
	                <button type="button" onClick={() => setIsModalOpen(p => ({...p, industry_class: true}))} className="p-2 border rounded-md text-left w-full hover:bg-gray-50 relative bg-white text-gray-900">{isEnglishBeta ? "Industry..." : "業種..."} <span className="absolute right-2 top-1/2 -translate-y-1/2 bg-indigo-100 text-indigo-700 font-semibold px-2 py-0.5 rounded-full">{countSelected('industry_class')}</span></button>
                  <label className="flex items-start gap-2 text-sm text-gray-600 md:col-span-2 lg:col-span-1">
                    <input
                      type="checkbox"
                      name="industryRelatedSearch"
                      checked={Boolean(filters.industryRelatedSearch)}
                      onChange={handleFilterChange}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>{isEnglishBeta ? "Include related words in company names and tags" : "会社名・タグなどの関連語も含める"}</span>
                  </label>
	                <button type="button" onClick={() => setIsModalOpen(p => ({...p, address: true}))} className="p-2 border rounded-md text-left w-full hover:bg-gray-50 relative bg-white text-gray-900">{isEnglishBeta ? "Location..." : "住所..."} <span className="absolute right-2 top-1/2 -translate-y-1/2 bg-indigo-100 text-indigo-700 font-semibold px-2 py-0.5 rounded-full">{countSelected('address')}</span></button>
	                <button type="button" onClick={() => setIsModalOpen(p => ({...p, business_tags: true}))} className="p-2 border rounded-md text-left w-full hover:bg-gray-50 relative bg-white text-gray-900">{isEnglishBeta ? "Business tags..." : "事業タグ..."} <span className="absolute right-2 top-1/2 -translate-y-1/2 bg-indigo-100 text-indigo-700 font-semibold px-2 py-0.5 rounded-full">{countSelected('business_tags')}</span></button>
                
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-1">{isEnglishBeta ? "Client execution history" : "自社送信履歴"}</label>
	                  <select name="sentStatus" value={filters.sentStatus} onChange={handleFilterChange} className="w-full p-2 border rounded-md bg-white text-gray-900">
                      <option value="all">{isEnglishBeta ? "All" : "すべて"}</option><option value="success">{isEnglishBeta ? "Submitted only" : "成功のみ"}</option><option value="needs_review">{isEnglishBeta ? "Needs Review only" : "要確認のみ"}</option><option value="fail">{isEnglishBeta ? "Failed only" : "失敗のみ"}</option><option value="unsent">{isEnglishBeta ? "Not started only" : "未送信のみ"}</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-1">{isEnglishBeta ? "Global execution history" : "全体送信履歴"}</label>
	                  <select name="globalSendStatus" value={filters.globalSendStatus} onChange={handleFilterChange} className="w-full p-2 border rounded-md bg-white text-gray-900" disabled={listSourceType === 'client'} title={listSourceType === 'client' ? "弊社提供リストでのみ使用可" : ""}>
                      <option value="all">{isEnglishBeta ? "All" : "すべて"}</option><option value="success">{isEnglishBeta ? "Submitted only" : "成功のみ"}</option><option value="fail">{isEnglishBeta ? "Failed only" : "失敗のみ"}</option><option value="needs_review">{isEnglishBeta ? "Needs Review only" : "要確認のみ"}</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-1">{isEnglishBeta ? "Global engagement events" : "全体クリック履歴"}</label>
	                  <select name="globalClickStatus" value={filters.globalClickStatus} onChange={handleFilterChange} className="w-full p-2 border rounded-md bg-white text-gray-900" disabled={listSourceType === 'client'} title={listSourceType === 'client' ? "弊社提供リストでのみ使用可" : ""}>
                      <option value="all">{isEnglishBeta ? "All" : "すべて"}</option><option value="yes">{isEnglishBeta ? "Has engagement" : "クリックあり"}</option><option value="no">{isEnglishBeta ? "No engagement" : "なし"}</option>
                  </select>
                </div>
            </div>
            
            <div className="mt-4 pt-4 border-t flex justify-stretch sm:justify-end">
		              <button type="submit" disabled={isLoading} className="w-full sm:w-auto justify-center flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-wait">
	                {isLoading ? <><LoadingSpinner /> {isEnglishBeta ? "Searching..." : "検索中..."}</> : <><SearchIcon /> {isEnglishBeta ? "Search targets" : "検索"}</>}
	              </button>
	            </div>
	          </form>
        </div>

         <div className="flex flex-col items-stretch gap-3 mb-4 lg:flex-row lg:items-center">
          <button onClick={handleSend} disabled={isSendButtonDisabled || isLoading} title={isAllMatchesSendPending ? (isEnglishBeta ? "The full target count is still being resolved." : "検索結果の総件数を確認中です。") : ""} className="flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"><SendIcon />{selectedItems.size > 0 ? (isEnglishBeta ? `Execute ${selectedItems.size.toLocaleString()} selected targets` : `${selectedItems.size.toLocaleString()}件 送信`) : (isCountingTotal ? (isEnglishBeta ? "Counting targets..." : "総件数確認中...") : (selectedSavedListId ? (isEnglishBeta ? "Execute saved-list targets" : "保存済みリスト内を送信") : (isEnglishBeta ? "Execute matched targets" : "フィルター条件で全件送信")))}</button>
          <button onClick={handleSelectAllOnPage} disabled={isLoading} className="bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-md hover:bg-gray-300 disabled:bg-gray-300 disabled:cursor-not-allowed">{isEnglishBeta ? "Select page" : "このページを全選択"}</button>
          <button onClick={handleClearAll} disabled={isLoading} className="bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-md hover:bg-gray-300 disabled:bg-gray-300 disabled:cursor-not-allowed">{isEnglishBeta ? "Clear selection" : "全解除"}</button>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 bg-white border border-gray-300 px-3 py-2 rounded-md hover:bg-gray-50 transition-colors">
            <input
              type="checkbox"
              checked={isRawMode}
              onChange={(e) => setIsRawMode(e.target.checked)}
              className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="font-medium text-red-600">{isEnglishBeta ? "Use copy exactly as written (tracking unavailable)" : "文面のURL等をそのまま送る（計測不可）"}</span>
          </label>
          <span className="text-sm text-gray-600 lg:ml-auto">
            {isEnglishBeta ? "Matched targets" : ""} <strong>{pagination ? pagination.totalItems.toLocaleString() : 0}</strong>{isEnglishBeta ? (isEstimatedResultCount ? "+" : "") : (isEstimatedResultCount ? "件以上" : "件中")}
            {isCountingTotal && <span className="ml-2 text-amber-600">{isEnglishBeta ? "Counting..." : "総件数確認中..."}</span>}
            {!isCountingTotal && totalCountError && <span className="ml-2 text-amber-600">{isEnglishBeta ? "Count not confirmed" : "総件数未確定"}</span>}
          </span>
        </div>
        
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6 lg:h-[65vh]">
          <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col relative min-h-[420px] lg:min-h-0">
            {isLoading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                <div className="flex items-center">
                  <LoadingSpinner />
                  <span className="text-lg font-semibold text-gray-700">{isEnglishBeta ? "Searching..." : "検索中..."}</span>
                </div>
              </div>
            )}
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">{isEnglishBeta ? "Target list" : "送信対象リスト"}</h2>
	              <div className="flex items-center gap-2"><label className="text-sm">{isEnglishBeta ? "Rows:" : "表示件数:"}</label><select value={itemsPerPage} onChange={handleItemsPerPageChange} className="p-1 border rounded-md bg-white text-gray-900 text-sm" disabled={isLoading}><option value={100}>100</option><option value="500">500</option><option value="1000">1000</option></select></div>
            </div>
            <div className="overflow-auto flex-grow">
              {!hasSearched ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <p>{isEnglishBeta ? "Set filters and select Search targets." : "検索条件を指定して、「検索」ボタンを押してください。"}</p>
                </div>
              ) : searchError && !isLoading ? (
                <div className="flex items-center justify-center h-full text-amber-700 text-center px-8">
                  <p>{searchError}</p>
                </div>
              ) : salesList.length === 0 && !isLoading ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <p>{isEnglishBeta ? "No targets matched the current filters." : "条件に一致するリストはありませんでした。"}</p>
                </div>
              ) : (
                <>
                <div className="divide-y md:hidden">
	                  {salesList.map((item) => {
	                    const isSelected = selectedItems.has(item.id);
	                    const isNg = checkIsNg(item);
	                    const myHistoryIcon = renderMyHistoryStatus(item.my_latest_status, isEnglishBeta);
	                    const canBeSelected = isSelected || selectedItems.size < remainingSends;
	                    const hasGlobalSent = Number(item.global_sent_count || 0) > 0;
	                    const hasGlobalSuccess = Number(item.global_success_count || 0) > 0;
	                    const hasGlobalReview = Number(item.global_review_count || 0) > 0;
	                    const hasGlobalClick = Number(item.global_click_count || 0) > 0;

	                    return (
                      <div
                        key={`${item.id}-${listSourceType}-mobile`}
                        role="button"
                        tabIndex={isNg ? -1 : 0}
                        onClick={() => { if (!isNg) setPreviewItem(item); }}
                        onKeyDown={(event) => { if (!isNg && (event.key === 'Enter' || event.key === ' ')) setPreviewItem(item); }}
                        className={`p-4 text-left ${isSelected ? "bg-blue-50" : ""} ${isNg ? "bg-red-50 text-gray-400" : "hover:bg-gray-50"}`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isNg || !canBeSelected}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => handleToggle(item)}
                            className="mt-1 h-4 w-4 disabled:cursor-not-allowed"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <p className="font-bold text-gray-900 break-words">
                                {item.company}
                                {isNg && <span className="ml-2 text-xs text-red-500">({isEnglishBeta ? "Suppressed" : "NG"})</span>}
                              </p>
                              <div className="shrink-0">{myHistoryIcon}</div>
                            </div>
	                            {listSourceType === 'master' && (
	                              <div className="mt-2 flex flex-wrap gap-1 text-xs">
	                                {hasGlobalSent ? (
	                                  <>
	                                    <span title={isEnglishBeta ? "Shared outreach history" : "全体送信履歴あり"} aria-label={isEnglishBeta ? "Shared outreach history" : "全体送信履歴あり"} className="bg-gray-200 px-2 py-0.5 rounded-full">📨</span>
	                                    {hasGlobalSuccess && <span title={isEnglishBeta ? "Shared submitted history" : "全体成功履歴あり"} aria-label={isEnglishBeta ? "Shared submitted history" : "全体成功履歴あり"} className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full">✅</span>}
	                                    {hasGlobalReview && <span title={isEnglishBeta ? "Shared needs-review history" : "全体要確認履歴あり"} aria-label={isEnglishBeta ? "Shared needs-review history" : "全体要確認履歴あり"} className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">⚠️</span>}
	                                    {hasGlobalClick && <span title={isEnglishBeta ? "Shared engagement history" : "全体クリック履歴あり"} aria-label={isEnglishBeta ? "Shared engagement history" : "全体クリック履歴あり"} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">🖱️</span>}
	                                  </>
	                                ) : (
	                                  <span className="text-gray-400">{isEnglishBeta ? "No global history" : "全体実績なし"}</span>
                                )}
                              </div>
                            )}
                            <div className="mt-3 grid grid-cols-1 gap-1 text-xs text-gray-600">
                              <p className="truncate">{isEnglishBeta ? <OperatorTaxonomyText value={item.industry_class} labelType="industry" /> : item.industry_class || "業種なし"}</p>
                              <p className="truncate">{isEnglishBeta ? <OperatorAddressText value={item.address} /> : item.address || "住所なし"}</p>
                              {(item.business_tags || item.list_tags) && <p className="truncate">{item.business_tags || item.list_tags}</p>}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <table className="hidden w-full text-sm table-fixed md:table">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="p-2 w-12 text-center">{isEnglishBeta ? "Select" : "選択"}</th>
                      <th className="p-2 text-left w-40">{isEnglishBeta ? "Company" : "会社名"}</th>
                      <th className="p-2 text-left w-24">{isEnglishBeta ? "Client history" : "自社履歴"}</th>
                      {listSourceType === 'master' && <th className="p-2 text-left w-40">{isEnglishBeta ? "Global history" : "全体履歴"}</th>}
                      <th className="p-2 text-left w-40">{isEnglishBeta ? "Industry" : "業種"}</th>
                      <th className="p-2 text-left w-44">{isEnglishBeta ? "Location" : "住所"}</th>
                      <th className="p-2 text-left w-40">{isEnglishBeta ? "Business tags" : "事業タグ"}</th>
                      <th className="p-2 text-left w-40">{isEnglishBeta ? "List tags" : "リストタグ"}</th>
                    </tr>
                  </thead>
                  <tbody>
	                    {salesList.map((item) => {
	                      const isSelected = selectedItems.has(item.id)
	                      const isNg = checkIsNg(item)
	                      const myHistoryIcon = renderMyHistoryStatus(item.my_latest_status, isEnglishBeta);
	                      const hasGlobalSent = Number(item.global_sent_count || 0) > 0;
	                      const hasGlobalSuccess = Number(item.global_success_count || 0) > 0;
	                      const hasGlobalReview = Number(item.global_review_count || 0) > 0;
	                      const hasGlobalClick = Number(item.global_click_count || 0) > 0;
	                      
	                      const canBeSelected = isSelected || selectedItems.size < remainingSends;

                      return (
                        <tr key={`${item.id}-${listSourceType}`} 
                            className={`${isSelected ? "bg-blue-50" : ""} ${isNg ? "bg-red-50 text-gray-400 cursor-not-allowed" : "hover:bg-gray-50 cursor-pointer"}`} 
                            onClick={() => { if (!isNg) { setPreviewItem(item); } }}>
                          <td className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
                              <input type="checkbox" checked={isSelected} disabled={isNg || !canBeSelected} onChange={() => handleToggle(item)} className="h-4 w-4 disabled:cursor-not-allowed"/>
                          </td>
                          <td className="p-2 font-medium truncate" title={item.company}>{item.company}{isNg && <span className="text-xs text-red-500 ml-2">({isEnglishBeta ? "Suppressed" : "NG"})</span>}</td>
                          <td className="p-2">{myHistoryIcon}</td>
	                          {listSourceType === 'master' && 
	                            <td className="p-2">
	                                <div className="flex items-center flex-wrap gap-1 text-xs">
	                                {hasGlobalSent ? (
	                                    <>
	                                    <span title={isEnglishBeta ? "Shared outreach history" : "全体送信履歴あり"} aria-label={isEnglishBeta ? "Shared outreach history" : "全体送信履歴あり"} className="bg-gray-200 px-2 py-0.5 rounded-full whitespace-nowrap">📨</span>
	                                    {hasGlobalSuccess && (
	                                        <span title={isEnglishBeta ? "Shared submitted history" : "全体成功履歴あり"} aria-label={isEnglishBeta ? "Shared submitted history" : "全体成功履歴あり"} className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full whitespace-nowrap">✅</span>
	                                    )}
	                                    {hasGlobalReview && (
	                                        <span title={isEnglishBeta ? "Shared needs-review history" : "全体要確認履歴あり"} aria-label={isEnglishBeta ? "Shared needs-review history" : "全体要確認履歴あり"} className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full whitespace-nowrap">⚠️</span>
	                                    )}
	                                    {hasGlobalClick && (
	                                        <span title={isEnglishBeta ? "Shared engagement history" : "全体クリック履歴あり"} aria-label={isEnglishBeta ? "Shared engagement history" : "全体クリック履歴あり"} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full whitespace-nowrap">🖱️</span>
	                                    )}
	                                    </>
	                                ) : (
                                    <span className="text-gray-400 text-xs">{isEnglishBeta ? "No history" : "実績なし"}</span>
                                )}
                                </div>
                            </td>
                          }
	                          <td className="p-2 truncate text-gray-700" title={item.industry_class}>{isEnglishBeta ? <OperatorTaxonomyText value={item.industry_class} labelType="industry" /> : item.industry_class}</td>
	                          <td className="p-2 truncate text-gray-700" title={item.address}>{isEnglishBeta ? <OperatorAddressText value={item.address} /> : item.address}</td>
	                          <td className="p-2 truncate text-gray-700" title={item.business_tags}>{isEnglishBeta ? <OperatorTaxonomyText value={item.business_tags} labelType="businessTag" /> : item.business_tags}</td>
	                          <td className="p-2 truncate text-gray-700" title={item.list_tags}>{item.list_tags}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                </>
              )}
            </div>
		            <div className="p-2 border-t"><Pagination currentPage={currentPage} totalPages={pagination ? pagination.totalPages : 0} hasNextPage={pagination?.hasNextPage} isEstimatedTotal={pagination?.isEstimatedTotal} onPageChange={handlePageChange} isLoading={isLoading} isEnglishBeta={isEnglishBeta} /></div>
          </div>

          <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col min-h-[420px] lg:min-h-0">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">{isEnglishBeta ? "Japanese outreach preview" : "プレビュー"}</h2>
              {isEnglishBeta && (
                <a href={JAPAN_GTM_ASSISTANT_URL} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-indigo-600 hover:underline">
                  Review Japanese outreach
                </a>
              )}
            </div>
            <div className="p-4 overflow-y-auto flex-grow">
              {previewItem ? (
                <div className="space-y-4">
	                  <div><h4 className="font-semibold text-gray-700">{isEnglishBeta ? "Subject:" : "件名:"}</h4><p className="mt-1 p-3 bg-gray-100 text-gray-900 rounded text-sm break-words">{previewSubject}</p></div>
	                  <div><h4 className="font-semibold text-gray-700">{isEnglishBeta ? "Body:" : "本文:"}</h4><pre className="mt-1 p-3 bg-gray-100 text-gray-900 rounded text-sm whitespace-pre-wrap font-sans break-words h-96 overflow-y-auto">{previewBody}</pre></div>
                </div>
              ) : <p className="text-gray-500">{isEnglishBeta ? "Select a target from the list to preview Japanese outreach copy." : "左のリストから項目を選択してプレビューを表示します。"}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SendPage;
